/**
 * Merchant → Storefronts (Loop P4)
 *
 * Multi-storefront + per-storefront province management. Reads from
 *   GET  /api/v1/merchant/storefronts
 *   POST /api/v1/merchant/storefronts
 *   POST /api/v1/merchant/storefronts/{sid}/provinces
 *   DELETE /api/v1/merchant/storefronts/{sid}
 *
 * Rules the UI enforces (in addition to backend):
 *   #19: plainly show current plan + included storefronts + province allowance
 *   #20: for each storefront, list province coverage (Included / Add-on)
 *   #21: pricing preview when adding a storefront
 *    #5: surface the duplicate-brand hard-block message in full
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Store, Plus, Trash2, ShieldAlert, MapPin, Info, Sparkles, X, Check, ArrowRight, TrendingUp, PlayCircle, Video, Search, Wand2, Edit3, Globe, Smartphone, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cad, usePricing } from "@/lib/pricing";
import { useAiEntitlement } from "@/hooks/useAiEntitlement";
import { AiUpgradeCta } from "@/components/merchant/AiUpgrade";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const PROV_NAMES = {
  AB: "Alberta", BC: "British Columbia", MB: "Manitoba", NB: "New Brunswick",
  NL: "Newfoundland and Labrador", NS: "Nova Scotia", NT: "Northwest Territories",
  NU: "Nunavut", ON: "Ontario", PE: "Prince Edward Island", QC: "Quebec",
  SK: "Saskatchewan", YT: "Yukon",
};



export default function MerchantStorefronts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showProvinces, setShowProvinces] = useState(null);   // storefront doc
  const [showSeo, setShowSeo] = useState(null);               // storefront doc
  const [busy, setBusy] = useState(false);
  const pricing = usePricing();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/merchant/storefronts");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Could not load storefronts");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancelStorefront = async (sid, name) => {
    if (!window.confirm(`Cancel storefront “${name}”?\n\nThe storefront becomes inactive at the end of your billing period. Campaigns, history and analytics are retained. You'll receive Stripe proration credit for the unused portion.`)) return;
    setBusy(true);
    try {
      await api.delete(`/v1/merchant/storefronts/${sid}`);
      toast.success(`${name} cancelled`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Could not cancel storefront");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="p-8 text-slate-500" data-testid="sf-loading">Loading storefronts…</div>;
  }
  if (!data) return null;

  const { storefronts, entitlement, addon_pricing } = data;
  const monthlySF = addon_pricing.storefront_monthly_cad;
  const annualSF  = addon_pricing.storefront_annual_cad;
  const monthlyPR = addon_pricing.province_monthly_cad;
  const annualPR  = addon_pricing.province_annual_cad;
  const isAnnual = addon_pricing.cadence === "annual";
  const nextSFPriceDisplay = isAnnual
    ? `${cad(annualSF)}/yr (15% off vs monthly)`
    : `${cad(monthlySF)}/mo`;

  // Growth upsell math (rule: shown to Starter merchants only, when their
  // add-on total per month is >= the Starter→Growth base-plan delta).
  const upsell = computeGrowthUpsell({ entitlement, addon_pricing, pricing });
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="storefronts-page">
      {/* First-run onboarding tour */}
      <OnboardingTour storefronts={storefronts} entitlement={entitlement} pricing={pricing} sfMonthly={monthlySF} />

      {/* Growth upsell badge */}
      {upsell && <GrowthUpsellBanner {...upsell} />}

      {/* Header + allowance summary */}
      <div className="pf-card p-6" data-testid="sf-header">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <h1 className="font-display text-3xl font-semibold text-slate-900 flex items-center gap-2">
              <Store className="w-7 h-7 text-burgundy-600" />
              Storefronts
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Plan:{" "}
              <span className="font-medium capitalize" data-testid="sf-current-plan">
                {entitlement.plan}
              </span>{" "}
              · Included:{" "}
              <span data-testid="sf-included-count">
                {entitlement.storefronts_used_included}/{entitlement.storefronts_included} storefronts used
              </span>{" "}
              · Up to{" "}
              <span data-testid="sf-included-provinces">
                {entitlement.storefronts[0]?.included_provinces || "?"}
              </span>{" "}
              included provinces per storefront
            </p>
            <p className="text-xs text-slate-500 mt-2 flex items-start gap-1">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              A storefront represents a distinct brand, division, service line
              or business unit. Do not create a duplicate storefront for the
              same brand — add provinces to your existing storefront instead
              (rule #5, enforced server-side).
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Link to="/merchant/storefront-setup"
                    className="text-xs font-semibold text-cyan-700 hover:text-cyan-800 inline-flex items-center gap-1"
                    data-testid="sf-setup-guide-link">
                Storefront Setup Guide →
              </Link>
              {entitlement.storefronts_used_included > 0 && (
                <Link to="/merchant/storefront-setup?additional=1"
                      className="text-xs font-semibold text-cyan-700 hover:text-cyan-800 inline-flex items-center gap-1"
                      data-testid="sf-add-another-link">
                  Add another Storefront (fast track) →
                </Link>
              )}
            </div>
          </div>
          <Button
            data-testid="sf-add-btn"
            onClick={() => setShowCreate(true)}
            className="bg-black hover:bg-burgundy-700 text-white"
          >
            <Plus className="w-4 h-4 mr-1" /> Add storefront
          </Button>
        </div>

        {(entitlement.storefront_addon_qty > 0 || entitlement.province_addon_qty > 0) && (
          <div className="mt-4 p-3 rounded-lg bg-cream-50 border border-cream-200 text-sm text-slate-700" data-testid="sf-addons-summary">
            <div className="font-medium text-slate-900 mb-1">Current add-ons</div>
            {entitlement.storefront_addon_qty > 0 && (
              <div>· {entitlement.storefront_addon_qty} extra storefront{entitlement.storefront_addon_qty > 1 ? "s" : ""} @ {cad(monthlySF)}/mo each</div>
            )}
            {entitlement.province_addon_qty > 0 && (
              <div>· {entitlement.province_addon_qty} extra province{entitlement.province_addon_qty > 1 ? "s" : ""} @ {cad(monthlyPR)}/mo each</div>
            )}
            {isAnnual && (
              <div className="mt-1 text-xs text-emerald-700 font-medium">Annual billing — 15% off applied to each add-on.</div>
            )}
          </div>
        )}
      </div>

      {/* Storefront cards */}
      <div className="grid gap-4">
        {storefronts.map((sf, idx) => {
          const ent = entitlement.storefronts.find((e) => e.id === sf.id) || {};
          const status = (sf.status || "DRAFT").toUpperCase();
          const isLive = status === "LIVE";
          const isPaused = status === "PAUSED";
          const isDraft = status === "DRAFT";

          return (
            <div key={sf.id} className="pf-card p-5 transition hover:shadow-sm" data-testid={`sf-card-${sf.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-display text-xl font-semibold text-slate-900 truncate" data-testid={`sf-name-${sf.id}`}>
                      {sf.name}
                    </h2>

                    {/* Status Badge */}
                    {isLive && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 gap-1 font-semibold" data-testid={`sf-status-${sf.id}`}>
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> LIVE
                      </Badge>
                    )}
                    {isPaused && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-300 gap-1 font-semibold" data-testid={`sf-status-${sf.id}`}>
                        <PauseCircle className="w-3.5 h-3.5 text-amber-600" /> PAUSED
                      </Badge>
                    )}
                    {isDraft && (
                      <Badge className="bg-slate-100 text-slate-700 border-slate-300 gap-1 font-semibold" data-testid={`sf-status-${sf.id}`}>
                        <span className="w-2 h-2 rounded-full bg-slate-400" /> DRAFT
                      </Badge>
                    )}
                    {!isLive && !isPaused && !isDraft && (
                      <Badge variant="outline" className="capitalize" data-testid={`sf-status-${sf.id}`}>{status.toLowerCase()}</Badge>
                    )}

                    {/* Plan Entitlement Badge */}
                    {ent.is_addon_storefront ? (
                      <Badge className="bg-cream-100 text-burgundy-700 border-burgundy-200">Add-on · {cad(monthlySF)}/mo</Badge>
                    ) : (
                      <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 font-medium">
                        Included {entitlement.plan === "growth" ? `(${idx + 1}/3)` : ""}
                      </Badge>
                    )}

                    {ent.grandfathered && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200">Grandfathered</Badge>
                    )}
                  </div>

                  {sf.short_description && (
                    <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{sf.short_description}</p>
                  )}

                  {/* Metadata Row: Destination / Website */}
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                    {sf.website_url && (
                      <a href={sf.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-700 hover:underline">
                        <Globe className="w-3 h-3" /> {sf.website_url.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                    {sf.purchase_destination_type && (
                      <span className="inline-flex items-center gap-1 text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
                        <Smartphone className="w-3 h-3 text-slate-400" />
                        {sf.purchase_destination_type.replace(/_/g, " ").toLowerCase()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap justify-end">
                  {/* Edit storefront link */}
                  <Link
                    to={`/merchant/storefront?sid=${sf.id}`}
                    className="inline-flex items-center justify-center border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm transition"
                    data-testid={`sf-edit-${sf.id}`}
                    title="Edit storefront details, products & settings"
                  >
                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                  </Link>

                  <Button
                    variant="outline" size="sm"
                    data-testid={`sf-manage-provinces-${sf.id}`}
                    onClick={() => setShowProvinces(sf)}
                  >
                    <MapPin className="w-3.5 h-3.5 mr-1" /> Provinces
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    data-testid={`sf-edit-seo-${sf.id}`}
                    onClick={() => setShowSeo(sf)}
                    title="Edit SEO title, description and OG image"
                  >
                    <Search className="w-3.5 h-3.5 mr-1" /> SEO
                  </Button>
                  {storefronts.length > 1 && (
                    <Button
                      variant="ghost" size="sm"
                      data-testid={`sf-cancel-${sf.id}`}
                      disabled={busy}
                      onClick={() => cancelStorefront(sf.id, sf.name)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Cancel storefront"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Province coverage row */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Province coverage · {ent.included_provinces || 0} included · {ent.addon_provinces || 0} add-on
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(sf.provinces || []).length === 0 && (
                    <span className="text-sm text-slate-500 italic">No provinces enabled yet — click Provinces to add.</span>
                  )}
                  {(sf.provinces || []).map((p, pi) => {
                    const isIncluded = pi < (ent.included_provinces || 0);
                    return (
                      <span
                        key={p}
                        data-testid={`sf-prov-${sf.id}-${p}`}
                        className={
                          "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium " +
                          (isIncluded
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-cream-50 text-burgundy-700 border border-burgundy-200")
                        }
                      >
                        {PROV_NAMES[p] || p}
                        {isIncluded ? " · Included" : " · Add-on"}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add-storefront dialog */}
      <AddStorefrontDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        pricingDisplay={nextSFPriceDisplay}
        provinceMonthly={monthlyPR}
        isAnnual={isAnnual}
        entitlement={entitlement}
        onSuccess={() => { setShowCreate(false); load(); }}
      />

      {/* Manage-provinces dialog */}
      <ManageProvincesDialog
        storefront={showProvinces}
        onClose={() => setShowProvinces(null)}
        addonPricing={{ monthly: monthlyPR, annual: annualPR, isAnnual }}
        entitlement={entitlement}
        onSaved={() => { setShowProvinces(null); load(); }}
      />

      {/* SEO editor dialog */}
      <SeoEditorDialog
        storefront={showSeo}
        onClose={() => setShowSeo(null)}
        onSaved={() => { setShowSeo(null); load(); }}
      />
    </div>
  );
}


// ─── Add-storefront dialog ─────────────────────────────────────────────
function AddStorefrontDialog({ open, onClose, pricingDisplay, provinceMonthly, isAnnual, entitlement, onSuccess }) {
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [category, setCategory] = useState("");
  const [saving, setSaving] = useState(false);
  const [blockError, setBlockError] = useState(null);
  const [addonProvinces, setAddonProvinces] = useState(3);

  useEffect(() => {
    if (open) {
      setName(""); setWebsite(""); setCategory(""); setBlockError(null);
      setAddonProvinces(entitlement.additional_starter_sf_provinces || 3);
    }
  }, [open, entitlement]);

  const willBeAddon = entitlement.storefronts_used >= entitlement.storefronts_included;

  const submit = async () => {
    setSaving(true); setBlockError(null);
    try {
      await api.post("/v1/merchant/storefronts", {
        name, website_url: website || undefined,
        primary_category: category || undefined,
        provinces: [],  // start empty — merchant sets after create
      });
      toast.success(`Storefront “${name}” created`);
      onSuccess();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && (d.code === "duplicate_brand_hard_block" || d.error === "duplicate_brand_hard_block")) {
        setBlockError(d);
      } else if (d && d.error === "subscription_required") {
        toast.error(d.message);
      } else {
        toast.error(d?.message || d || "Could not create storefront");
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-[#240710] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-[#FFFDF7] shadow-2xl"
        data-testid="add-storefront-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-[#FFFDF7]">
            <Sparkles className="w-5 h-5 text-burgundy-600 dark:text-rose-400" /> Add a new storefront
          </DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400 text-sm">
            Each storefront must represent a distinct brand, division or business unit.
          </DialogDescription>
        </DialogHeader>

        {willBeAddon ? (
          <div
            className="p-3.5 rounded-xl bg-cream-50 dark:bg-[#3A0F19] border border-cream-200 dark:border-white/10 text-sm text-slate-800 dark:text-slate-200"
            data-testid="add-sf-pricing-banner"
          >
            <div className="font-medium text-slate-900 dark:text-[#FFFDF7] flex items-center justify-between">
              <span>Additional storefront</span>
              <span className="font-semibold text-burgundy-700 dark:text-rose-300">{pricingDisplay}</span>
            </div>
            <div className="text-xs mt-1.5 text-slate-600 dark:text-slate-400 leading-relaxed">
              Includes {entitlement.additional_starter_sf_provinces || 3} province coverage.
              Extra provinces are billed at {isAnnual ? "the discounted annual add-on price" : `${cad(provinceMonthly)}/mo each`}.
            </div>
          </div>
        ) : (
          <div className="p-3.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 text-sm text-emerald-900 dark:text-emerald-200 flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span className="font-medium">Included in your plan · no extra charge.</span>
          </div>
        )}

        {blockError && (
          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 text-sm text-slate-900 dark:text-slate-100" data-testid="add-sf-dup-error">
            <div className="flex items-start gap-2.5">
              <ShieldAlert className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-red-800 dark:text-red-300">{blockError.title || "Duplicate brand blocked"}</div>
                <p className="mt-1 text-xs text-red-700 dark:text-red-200/90 whitespace-pre-line leading-relaxed">{blockError.message}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3.5">
          <div>
            <Label htmlFor="new-sf-name" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Storefront name *
            </Label>
            <Input
              id="new-sf-name"
              data-testid="add-sf-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. ABC Fashion"
              className="mt-1.5 bg-white dark:bg-[#1A0509]/80 border-slate-200 dark:border-white/15 text-slate-900 dark:text-[#FFFDF7] placeholder:text-slate-400 dark:placeholder:text-slate-500 focus-visible:ring-burgundy-500/30 dark:focus-visible:ring-burgundy-400/40"
            />
          </div>
          <div>
            <Label htmlFor="new-sf-website" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Website (optional)
            </Label>
            <Input
              id="new-sf-website"
              data-testid="add-sf-website-input"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://"
              className="mt-1.5 bg-white dark:bg-[#1A0509]/80 border-slate-200 dark:border-white/15 text-slate-900 dark:text-[#FFFDF7] placeholder:text-slate-400 dark:placeholder:text-slate-500 focus-visible:ring-burgundy-500/30 dark:focus-visible:ring-burgundy-400/40"
            />
          </div>
          <div>
            <Label htmlFor="new-sf-cat" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Primary category (optional)
            </Label>
            <Input
              id="new-sf-cat"
              data-testid="add-sf-cat-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="retail, fashion, food, etc."
              className="mt-1.5 bg-white dark:bg-[#1A0509]/80 border-slate-200 dark:border-white/15 text-slate-900 dark:text-[#FFFDF7] placeholder:text-slate-400 dark:placeholder:text-slate-500 focus-visible:ring-burgundy-500/30 dark:focus-visible:ring-burgundy-400/40"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="add-sf-cancel"
            className="border-slate-200 dark:border-white/15 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 dark:hover:text-white"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving || !name.trim()}
            data-testid="add-sf-submit"
            className="bg-black hover:bg-burgundy-700 text-white dark:bg-[#FFFDF7] dark:hover:bg-white dark:text-[#1A0509] dark:hover:text-black font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? "Creating…" : (willBeAddon ? `Create · ${pricingDisplay}` : "Create (included)")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Manage-provinces dialog ──────────────────────────────────────────
const ALL_PROVINCES = ["ON", "AB", "BC", "QC", "MB", "SK", "NS", "NB", "NL", "PE", "YT", "NT", "NU"];

function ManageProvincesDialog({ storefront, onClose, addonPricing, entitlement, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const ent = useMemo(
    () => (entitlement?.storefronts || []).find((e) => e.id === storefront?.id) || {},
    [entitlement, storefront]
  );

  useEffect(() => {
    if (storefront) setSelected([...(storefront.provinces || [])]);
  }, [storefront]);

  if (!storefront) return null;

  const toggle = (p) => {
    setSelected((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  };

  const included = ent.included_provinces || 0;
  const extraCount = Math.max(0, selected.length - included);
  const extraCost = extraCount * Number(addonPricing.isAnnual ? addonPricing.annual : addonPricing.monthly);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/v1/merchant/storefronts/${storefront.id}/provinces`, {
        provinces: selected,
      });
      toast.success("Province coverage updated");
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || d || "Could not save provinces");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!storefront} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-[#240710] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-[#FFFDF7] shadow-2xl"
        data-testid="manage-provinces-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-[#FFFDF7]">
            <MapPin className="w-5 h-5 text-burgundy-600 dark:text-rose-400" />
            Province coverage · {storefront.name}
          </DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400 text-sm">
            Each storefront includes {included} province{included !== 1 ? "s" : ""}. Extra
            provinces are {addonPricing.isAnnual ? cad(addonPricing.annual) + "/yr" : cad(addonPricing.monthly) + "/mo"} each.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="province-picker">
          {ALL_PROVINCES.map((p) => {
            const isSelected = selected.includes(p);
            const wouldBeIncluded = isSelected && selected.indexOf(p) < included;
            return (
              <button
                type="button"
                key={p}
                onClick={() => toggle(p)}
                data-testid={`province-toggle-${p}`}
                className={
                  "text-left border rounded-lg px-3 py-2 text-sm transition font-medium " +
                  (isSelected
                    ? (wouldBeIncluded
                        ? "border-emerald-300 dark:border-emerald-700/80 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200 shadow-sm"
                        : "border-burgundy-300 dark:border-rose-900/60 bg-cream-50 dark:bg-[#3A0F19] text-burgundy-900 dark:text-rose-200 shadow-sm")
                    : "border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 text-slate-700 dark:text-slate-300 bg-transparent hover:bg-slate-50 dark:hover:bg-white/[0.04]")
                }
              >
                <div className="flex items-center justify-between gap-1.5">
                  <span className="truncate">{PROV_NAMES[p]}</span>
                  {isSelected && (
                    wouldBeIncluded ? (
                      <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5 text-burgundy-600 dark:text-rose-300 shrink-0" />
                    )
                  )}
                </div>
                <div className={
                  "text-xs mt-0.5 " +
                  (isSelected
                    ? (wouldBeIncluded ? "text-emerald-700 dark:text-emerald-400" : "text-burgundy-700 dark:text-rose-300/80")
                    : "text-slate-500 dark:text-slate-400")
                }>
                  {p}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-3.5 p-3.5 rounded-xl bg-slate-50 dark:bg-[#3A0F19] border border-slate-200 dark:border-white/10 text-sm text-slate-800 dark:text-slate-200" data-testid="province-summary">
          <div className="flex items-center justify-between">
            <span className="text-slate-600 dark:text-slate-400">Included:</span>
            <span className="font-semibold text-slate-900 dark:text-[#FFFDF7]">{Math.min(selected.length, included)} / {included}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-slate-600 dark:text-slate-400">Add-on provinces:</span>
            <span className="font-semibold text-burgundy-700 dark:text-rose-300">{extraCount}</span>
          </div>
          {extraCount > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-white/10 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Adds <span className="font-medium text-slate-900 dark:text-slate-100">{cad(extraCost)}{addonPricing.isAnnual ? "/yr" : "/mo"}</span> to your Stripe subscription.
              {addonPricing.isAnnual && " (Annual — 15% discount already applied.)"}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="manage-provinces-cancel"
            className="border-slate-200 dark:border-white/15 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 dark:hover:text-white"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="manage-provinces-save"
            className="bg-black hover:bg-burgundy-700 text-white dark:bg-[#FFFDF7] dark:hover:bg-white dark:text-[#1A0509] dark:hover:text-black font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : (extraCount > 0 ? `Save · +${cad(extraCost)}${addonPricing.isAnnual ? "/yr" : "/mo"}` : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── First-run onboarding tour ─────────────────────────────────────────
const TOUR_KEY = "cy_storefront_tour_v1_dismissed";
const VIDEO_URL = process.env.REACT_APP_MERCHANT_ONBOARDING_VIDEO_URL || "";

function isYouTubeUrl(url) {
  return /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))/.test(url || "");
}

function toEmbedUrl(url) {
  const m = url.match(/(?:youtu\.be\/|watch\?v=|embed\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?rel=0` : url;
}

function OnboardingVideoCard() {
  const [open, setOpen] = useState(false);
  const hasVideo = Boolean(VIDEO_URL);

  return (
    <>
      <div
        className="pf-card p-4 border border-slate-200 bg-slate-50/60 flex flex-wrap items-center gap-4"
        data-testid="sf-onboarding-video-card"
      >
        <div className="hidden sm:grid shrink-0 w-16 h-16 rounded-lg bg-gradient-to-br from-burgundy-100 to-cream-100 place-items-center">
          <Video className="w-7 h-7 text-burgundy-600" />
        </div>
        <div className="flex-1 min-w-[12rem]">
          <div className="text-sm font-semibold text-slate-900">
            Watch: setting up your Cyannect storefront (60&nbsp;seconds)
          </div>
          <div className="text-xs text-slate-600">
            {hasVideo
              ? "See a real merchant publish their storefront and first offer before you add anything."
              : "60-second walkthrough — coming soon. Ask hello@cyannect.com if you'd like an early preview."}
          </div>
        </div>
        <button
          onClick={() => hasVideo && setOpen(true)}
          disabled={!hasVideo}
          data-testid="sf-onboarding-video-open"
          className={
            "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold " +
            (hasVideo
              ? "bg-burgundy-600 text-white hover:bg-burgundy-700"
              : "bg-slate-200 text-slate-500 cursor-not-allowed")
          }
        >
          <PlayCircle className="w-4 h-4" />
          {hasVideo ? "Play" : "Coming soon"}
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl p-0 overflow-hidden"
          data-testid="sf-onboarding-video-dialog"
        >
          <DialogHeader className="p-4 pb-2">
            <DialogTitle>Cyannect merchant · 60-second tour</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black">
            {isYouTubeUrl(VIDEO_URL) ? (
              <iframe
                src={toEmbedUrl(VIDEO_URL)}
                title="Cyannect merchant onboarding video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                src={VIDEO_URL}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function OnboardingTour({ storefronts, entitlement, pricing, sfMonthly }) {
  const [dismissed, setDismissed] = useState(
    () => (typeof window !== "undefined" && window.localStorage.getItem(TOUR_KEY)) === "1"
  );
  if (dismissed) return null;

  // Step detection based on live state — merchant "graduates" as they hit each.
  const hasProvinces = (storefronts[0]?.provinces || []).length > 0;
  const isPublished  = storefronts[0]?.status === "LIVE";
  const hasSecondSF  = storefronts.length > 1;
  const plan = pricing?.final_active ? pricing.plans.find((p) => p.id === (entitlement.plan || "basic")) : null;
  const planName = plan?.name || (entitlement.plan === "growth" ? "Growth" : "Starter");
  const included = entitlement.storefronts[0]?.included_provinces || 1;

  const steps = [
    {
      id: "provinces",
      title: "Add province coverage",
      body:
        "Cyannect delivers offers to shoppers in the provinces you enable. " +
        `Your ${planName} plan includes ${included} ` +
        `province${included === 1 ? "" : "s"} per storefront — click Provinces on your storefront card to pick.`,
      done: hasProvinces,
    },
    {
      id: "publish",
      title: "Publish your storefront",
      body:
        "Give your storefront a name, primary category and logo, then click " +
        "Publish. Cyannect only surfaces LIVE storefronts to shoppers.",
      done: isPublished,
    },
    {
      id: "second-sf",
      title: "Add another brand (optional)",
      body:
        entitlement.plan === "growth"
          ? `${planName} includes ${plan?.storefronts_included || 3} storefronts. Add another distinct brand or division here.`
          : `Have a second, genuinely distinct brand? Additional storefronts are ${cad(sfMonthly)}/month and bundle up to 3 provinces.`,
      done: hasSecondSF,
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;

  const dismiss = () => {
    try { window.localStorage.setItem(TOUR_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className="pf-card p-5 border-2 border-cream-200 bg-gradient-to-br from-cream-50 via-white to-white relative"
      data-testid="sf-onboarding-tour"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
        aria-label="Dismiss onboarding tour"
        data-testid="sf-tour-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-burgundy-600" />
        <h3 className="font-display text-lg font-semibold text-slate-900">
          Get your storefront live
        </h3>
        <span
          className="ml-auto mr-6 text-xs font-medium text-slate-600"
          data-testid="sf-tour-progress"
        >
          {doneCount}/{steps.length} done
        </span>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Three quick steps and shoppers can start finding your offers on Cyannect.
      </p>
      <div className="mb-3">
        <OnboardingVideoCard />
      </div>
      <ol className="space-y-2">
        {steps.map((s, i) => (
          <li
            key={s.id}
            data-testid={`sf-tour-step-${s.id}`}
            className={
              "flex items-start gap-3 rounded-lg border px-3 py-2 " +
              (s.done
                ? "border-emerald-200 bg-emerald-50"
                : "border-slate-200 bg-white")
            }
          >
            <div
              className={
                "shrink-0 h-6 w-6 rounded-full grid place-items-center text-xs font-semibold " +
                (s.done
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-200 text-slate-700")
              }
            >
              {s.done ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">{s.title}</div>
              <div className="text-xs text-slate-600">{s.body}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}


// ─── Growth upsell banner ──────────────────────────────────────────────
/**
 * Rule: show ONLY to Starter merchants whose current monthly add-on spend
 * is greater-than-or-equal to the Starter→Growth base-plan delta. When true,
 * Growth is strictly cheaper AND unlocks better fees (2.85% vs 4.25%).
 */
function computeGrowthUpsell({ entitlement, addon_pricing, pricing }) {
  if (entitlement.plan !== "starter") return null;
  const plans = pricing?.final_active ? pricing.plans : null;
  const st = plans?.find((p) => p.id === "starter");
  const gr = plans?.find((p) => p.id === "growth");

  const isAnnual = addon_pricing.cadence === "annual";
  const provRate = Number(isAnnual ? addon_pricing.province_annual_cad : addon_pricing.province_monthly_cad);
  const sfRate   = Number(isAnnual ? addon_pricing.storefront_annual_cad : addon_pricing.storefront_monthly_cad);
  const addonSpend = entitlement.province_addon_qty * provRate
                    + entitlement.storefront_addon_qty * sfRate;

  // Base-plan spread from the live catalog (legacy: CA$39.99 vs CA$79.99 monthly). Annual = 12x monthly.
  const monthlyDelta = st && gr ? Number(gr.monthly_cad) - Number(st.monthly_cad) : 79.99 - 39.99;
  const delta = isAnnual ? monthlyDelta * 12 : monthlyDelta;

  if (addonSpend < delta) return null;

  const savings = addonSpend - delta;
  const period = isAnnual ? "/yr" : "/mo";
  const copy = gr
    ? { name: gr.name, bundle: `${gr.storefronts_included} storefronts × ${gr.provinces_included_per_storefront} provinces each`, from: st.fee_pct, to: gr.fee_pct }
    : { name: "Growth", bundle: "3 storefronts × 3 provinces each", from: "4.25", to: "2.85" };
  return { addonSpend, delta, savings, period, isAnnual, copy };
}

function GrowthUpsellBanner({ addonSpend, delta, savings, period, isAnnual, copy }) {
  return (
    <div
      className="pf-card p-5 relative overflow-hidden border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-cream-50"
      data-testid="sf-growth-upsell-banner"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-11 h-11 rounded-full bg-emerald-600 grid place-items-center">
          <TrendingUp className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg font-semibold text-slate-900" data-testid="sf-upsell-title">
              {copy.name} is cheaper for you right now
            </h3>
            <span className="inline-flex items-center rounded-full bg-emerald-600 text-white text-xs font-semibold px-2.5 py-0.5">
              Save {cad(savings)}{period}
            </span>
          </div>
          <p className="text-sm text-slate-700 mt-1">
            You're paying <strong>{cad(addonSpend)}{period}</strong> in Starter
            add-ons. {copy.name} is only <strong>{cad(delta)}{period}</strong> more than
            Starter and includes <strong>{copy.bundle}</strong> —
            no add-ons needed{isAnnual ? " (annual)" : ""}. Fees drop from{" "}
            <strong>{copy.from}%</strong> to <strong>{copy.to}%</strong> per verified sale, too.
          </p>
          <a
            href="/merchant/billing"
            data-testid="sf-upsell-cta"
            className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-emerald-800 hover:text-emerald-900 hover:underline"
          >
            Upgrade to {copy.name} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}



// ─── SEO editor dialog ─────────────────────────────────────────────────
/**
 * Merchant-facing SEO override editor. Writes to
 *   PATCH /api/v1/merchant/storefront?sid=<id>
 *   { seo_title, seo_description, og_image_url }
 * All three fields are optional — empty string clears the override so the
 * auto-generated defaults on `/store/<slug>` kick back in.
 */
function SeoEditorDialog({ storefront, onClose, onSaved }) {
  const { aiEnabled } = useAiEntitlement();
  const open = !!storefront;
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [ogUrl, setOgUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);   // {title, description, model_used, tone}
  const [tone, setTone] = useState("neutral");

  useEffect(() => {
    if (!storefront) return;
    setTitle(storefront.seo_title || "");
    setDesc(storefront.seo_description || "");
    setOgUrl(storefront.og_image_url || "");
    setSaving(false);
    setSuggesting(false);
    setSuggestion(null);
    setTone("neutral");
  }, [storefront?.id]);

  if (!storefront) return null;

  const slug = storefront.slug || storefront.id;
  const previewTitle = (title || `${storefront.name} coupons & offers · Cyannect`).slice(0, 70);
  const previewDesc = (
    desc || storefront.short_description ||
    `Discover verified ${storefront.primary_category || "brand"} coupons and cashback offers from ${storefront.name} on Cyannect — Canada's performance-based offer marketplace.`
  ).slice(0, 160);
  const previewImg = ogUrl || storefront.banner_image || storefront.logo_url || "";

  const titleLen = title.length;
  const descLen = desc.length;
  const titleWarn = titleLen > 60;
  const descWarn  = descLen > 155;

  async function save() {
    setSaving(true);
    try {
      await api.patch(
        `/v1/merchant/storefront?sid=${encodeURIComponent(storefront.id)}`,
        {
          seo_title: title.trim(),
          seo_description: desc.trim(),
          og_image_url: ogUrl.trim(),
        },
      );
      toast.success("SEO settings saved. Google will pick up the changes on next crawl.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save SEO settings.");
    } finally {
      setSaving(false);
    }
  }

  // Loop MWEB-2 · AI-assisted SEO suggestions.
  // Rule J: proposes ONLY — never auto-saves. Merchant must click "Save".
  // Rule H: uses the shared Emergent LLM abstraction (see backend
  // routers/storefronts.py::merchant_seo_suggest).
  async function requestSuggestion() {
    setSuggesting(true);
    try {
      const { data } = await api.post(
        `/v1/merchant/storefront/${encodeURIComponent(storefront.id)}/seo-suggest`,
        { tone },
      );
      setSuggestion(data.suggestion ? {
        ...data.suggestion,
        model_used: data.model_used,
        tone: data.tone,
      } : null);
      toast.success("AI suggestion ready — review and edit before saving.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "AI suggestion unavailable right now.");
    } finally { setSuggesting(false); }
  }

  function applySuggestion() {
    if (!suggestion) return;
    setTitle(String(suggestion.title || "").slice(0, 70));
    setDesc(String(suggestion.description || "").slice(0, 200));
    toast.info("Suggestion applied — review and click Save to publish.");
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="seo-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-burgundy-600" />
            SEO · {storefront.name}
          </DialogTitle>
          <DialogDescription>
            These override the auto-generated tags on{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">/store/{slug}</code>.
            Leave any field blank to use the smart default. Google typically shows
            the first ~60 characters of the title and ~155 characters of the
            description.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Loop MWEB-2 · AI-assisted SEO suggestions (rule H,J,K). */}
          <div
            className="rounded-xl border border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-emerald-50 p-4"
            data-testid="seo-ai-suggest-panel"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                  <Wand2 className="w-4 h-4 text-cyan-700" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">AI-assisted SEO suggestions</div>
                  <div className="text-xs text-slate-500">
                    Generate a search-friendly title + meta description from your brand — review before saving.
                  </div>
                </div>
              </div>
              {aiEnabled ? (
              <div className="flex items-center gap-2">
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="text-xs rounded-md border border-slate-300 bg-white px-2 py-1"
                  data-testid="seo-ai-tone"
                  aria-label="Tone"
                >
                  <option value="neutral">Neutral</option>
                  <option value="playful">Playful</option>
                  <option value="premium">Premium</option>
                  <option value="urgent">Urgent</option>
                </select>
                <Button
                  size="sm"
                  onClick={requestSuggestion}
                  disabled={suggesting}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 text-xs"
                  data-testid="seo-ai-suggest-btn"
                >
                  <Wand2 className="w-3 h-3 mr-1" />
                  {suggesting ? "Generating…" : "Auto-fill from brand"}
                </Button>
              </div>
              ) : <AiUpgradeCta testid="seo-ai-locked" />}
            </div>

            {suggestion && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="seo-ai-suggestion">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                  Suggested title
                </div>
                <div className="text-sm text-slate-900" data-testid="seo-ai-suggested-title">
                  {suggestion.title}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-2 mb-1">
                  Suggested description
                </div>
                <div className="text-sm text-slate-700" data-testid="seo-ai-suggested-desc">
                  {suggestion.description}
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={applySuggestion}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                    data-testid="seo-ai-apply"
                  >
                    <Check className="w-3 h-3 mr-1" /> Use this suggestion
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={requestSuggestion}
                    disabled={suggesting}
                    className="h-8 text-xs"
                    data-testid="seo-ai-regenerate"
                  >
                    Regenerate
                  </Button>
                  <span className="text-[10px] text-slate-400" data-testid="seo-ai-model">
                    {suggestion.model_used ? `via ${suggestion.model_used}` : ""}
                  </span>
                </div>
                <div className="mt-2 text-[10px] text-slate-500">
                  Nothing is published until you click <b>Save SEO settings</b> below.
                </div>
              </div>
            )}
          </div>

          {/* SEO title */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs uppercase tracking-wide text-slate-500">
                SEO Title
              </label>
              <span
                className={`text-xs font-mono ${titleWarn ? "text-amber-600" : "text-slate-400"}`}
                data-testid="seo-title-counter"
              >
                {titleLen}/60
              </span>
            </div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 70))}
              placeholder={`${storefront.name} coupons & offers · Cyannect`}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-burgundy-500"
              data-testid="seo-title-input"
              maxLength={70}
            />
            <p className="text-xs text-slate-500 mt-1">
              Include your brand name + a keyword shoppers actually search
              (e.g. "coupons", "cashback", city or province).
            </p>
          </div>

          {/* SEO description */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs uppercase tracking-wide text-slate-500">
                Meta Description
              </label>
              <span
                className={`text-xs font-mono ${descWarn ? "text-amber-600" : "text-slate-400"}`}
                data-testid="seo-desc-counter"
              >
                {descLen}/155
              </span>
            </div>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value.slice(0, 200))}
              placeholder={`Discover verified coupons and cashback offers from ${storefront.name}…`}
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-burgundy-500 resize-y"
              data-testid="seo-desc-input"
              maxLength={200}
            />
          </div>

          {/* OG image */}
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">
              Social Share Image (OG Image)
            </label>
            <input
              value={ogUrl}
              onChange={(e) => setOgUrl(e.target.value.slice(0, 500))}
              placeholder="https://cdn.mybrand.com/social-share.png"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-burgundy-500"
              data-testid="seo-og-input"
              maxLength={500}
              type="url"
            />
            <p className="text-xs text-slate-500 mt-1">
              1200 × 630 px works everywhere (Facebook, X, LinkedIn, Google).
              Leave blank to use your banner or logo.
            </p>
          </div>

          {/* Live preview */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="seo-preview">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
              Google preview
            </div>
            <div className="text-[10px] text-emerald-700 truncate">
              https://www.cyannect.com › store › {slug}
            </div>
            <div
              className="text-[#1a0dab] text-lg font-normal leading-tight truncate mt-0.5"
              data-testid="seo-preview-title"
            >
              {previewTitle}
            </div>
            <div className="text-sm text-slate-700 leading-snug mt-0.5 line-clamp-2" data-testid="seo-preview-desc">
              {previewDesc}
            </div>
            {previewImg && (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Social share preview
                </div>
                <div className="aspect-[1200/630] max-w-xs bg-white border border-slate-200 rounded-md overflow-hidden">
                  <img
                    src={previewImg}
                    alt="Social share preview"
                    className="w-full h-full object-cover"
                    data-testid="seo-preview-og"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="seo-editor-cancel">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-black hover:bg-burgundy-700 text-white"
            data-testid="seo-editor-save"
          >
            {saving ? "Saving…" : "Save SEO settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



